"use client";

import { useState } from "react";
import { toast } from "sonner";
import { useLocale, useTranslations } from "use-intl";

import { getTelegramConnectionState } from "@/components/telegram/telegram-connection-state";
import { useConsumeTelegramConnectionCallback } from "@/hooks/use-consume-telegram-connection-callback";
import { useDistributionAvailability } from "@/hooks/use-distribution-availability";
import { useViewer } from "@/hooks/use-viewer";
import { setLocale } from "@/i18n/actions";
import {
  trackNotificationChannelSetupFailed,
  trackNotificationChannelSetupStarted,
} from "@/lib/analytics";
import { authClient } from "@/lib/auth-client";
import {
  buildTelegramConnectionCallbackHref,
  buildTelegramSignupOnboardingHrefWithRedirect,
} from "@/lib/redirect-after-login";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

const TELEGRAM_PROVIDER_ID = "telegram-oidc";

/** Owns the shared Telegram authorization state for one rendered connection surface. */
export function useTelegramConnectionAction(callbackURL: string) {
  const t = useTranslations("telegram");
  const locale = useLocale() as ViewerLocale;
  const viewer = useViewer();
  const [connecting, setConnecting] = useState(false);
  const distributionQuery = useDistributionAvailability();
  const telegram = distributionQuery.data?.telegram;
  const connectionState = viewer.isMember
    ? getTelegramConnectionState(telegram?.connection)
    : "unlinked";
  const telegramCallbackURL = buildTelegramConnectionCallbackHref(callbackURL);
  const telegramConnectionConfirmed = connectionState === "alerts-enabled";

  useConsumeTelegramConnectionCallback(telegramConnectionConfirmed, viewer.userId);

  async function connectTelegram() {
    setConnecting(true);
    trackNotificationChannelSetupStarted("telegram", "connection");
    try {
      if (!viewer.isMember) await setLocale({ data: locale });
      const result = viewer.isMember
        ? await authClient.linkSocial({
            provider: TELEGRAM_PROVIDER_ID,
            callbackURL: telegramCallbackURL,
          })
        : await authClient.signIn.social({
            provider: TELEGRAM_PROVIDER_ID,
            callbackURL: telegramCallbackURL,
            newUserCallbackURL: buildTelegramSignupOnboardingHrefWithRedirect(telegramCallbackURL),
          });
      if (!result.error) return;
    } catch {
      // The localized toast below owns the user-visible provider failure.
    }
    setConnecting(false);
    trackNotificationChannelSetupFailed("telegram", "connection", "provider");
    toast.error(t("connectionError"));
  }

  return {
    available: viewer.capabilities.telegramAuthAvailable,
    connecting,
    connectionState,
    loading:
      !distributionQuery.data ||
      distributionQuery.isLoading ||
      telegram?.connectionStatus === "unavailable",
    onConnect: connectTelegram,
  } as const;
}
